/** Quién está en el portal y qué departamentos hay.
 *
 * Quien no gestione miembros ve la lista igual: saber a quién dirigirte es media herramienta.
 * Lo que no ve son los controles para cambiar nada.
 */
import { useCallback, useEffect, useState } from 'react'

import { ErrorDeApi, api } from '../api'
import { Avatar, Cargando, Dialogo, Error, Vacio } from '../componentes/piezas'
import { usePortal } from '../contexto'
import { PAPEL, PAPEL_EXPLICADO } from '../textos'
import type { Miembro, Papel, Persona } from '../tipos'

export default function Equipo() {
  const { ruta, departamentos, recargarDepartamentos, puedo, avisar } = usePortal()
  const [miembros, setMiembros] = useState<Miembro[] | null>(null)
  const [error, setError] = useState('')
  const [dialogo, setDialogo] = useState<'alta' | 'departamento' | null>(null)
  const gestiona = puedo('gestionar_miembros')

  const cargar = useCallback(async () => {
    try {
      setMiembros(await api.get<Miembro[]>(`${ruta}/equipo`))
      setError('')
    } catch (e) {
      setError((e as ErrorDeApi).message)
    }
  }, [ruta])

  useEffect(() => {
    void cargar()
  }, [cargar])

  async function cambiarPapel(miembro: Miembro, papel: Papel) {
    try {
      await api.patch(`${ruta}/equipo/${miembro.id}`, { papel })
      avisar(`${miembro.persona.nombre} ahora es ${PAPEL[papel].toLowerCase()}.`, 'bien')
      void cargar()
    } catch (e) {
      avisar((e as ErrorDeApi).message, 'malo')
    }
  }

  async function darDeBaja(miembro: Miembro) {
    try {
      await api.borrar(`${ruta}/equipo/${miembro.id}`)
      avisar(`${miembro.persona.nombre} ya no tiene acceso al portal.`, 'bien')
      void cargar()
    } catch (e) {
      avisar((e as ErrorDeApi).message, 'malo')
    }
  }

  async function cambiarPertenencia(miembro: Miembro, departamentoId: number, dentro: boolean,
                                    dirige: boolean) {
    try {
      if (dentro) {
        await api.put(`${ruta}/equipo/${miembro.id}/departamento`, {
          departamento_id: departamentoId, dirige,
        })
      } else {
        await api.borrar(`${ruta}/equipo/${miembro.id}/departamento/${departamentoId}`)
      }
      void cargar()
    } catch (e) {
      avisar((e as ErrorDeApi).message, 'malo')
    }
  }

  return (
    <>
      <header className="cabecera">
        <h1>Equipo</h1>
        <div className="cabecera__acciones">
          {puedo('gestionar_departamentos') && (
            <button className="boton" onClick={() => setDialogo('departamento')}>
              Nuevo departamento
            </button>
          )}
          {gestiona && (
            <button className="boton boton--principal" onClick={() => setDialogo('alta')}>
              Dar acceso
            </button>
          )}
        </div>
      </header>

      <div className="contenido">
        {error && <Error>{error}</Error>}

        <section className="tarjeta" style={{ marginBottom: 16 }}>
          <div className="tarjeta__titulo">Departamentos</div>
          {departamentos.length === 0 ? (
            <Vacio titulo="Todavía no hay departamentos" icono="⚑">
              <p>Policía, EMS, Bombers, Mecánicos… Créalos y podrás repartir las solicitudes.</p>
            </Vacio>
          ) : (
            <ul className="lista">
              {departamentos.map((d) => (
                <li className="fila" key={d.id}>
                  <span className="punto" style={{ color: d.color }} aria-hidden="true" />
                  <div className="fila__principal">
                    <div className="fila__titulo">{d.nombre}</div>
                    <div className="fila__meta">
                      <span className="codigo">{d.slug}</span>
                      {d.descripcion && <span>{d.descripcion}</span>}
                    </div>
                  </div>
                  <span className="etiqueta">
                    {d.solicitudes_abiertas} {d.solicitudes_abiertas === 1 ? 'abierta' : 'abiertas'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="tarjeta">
          <div className="tarjeta__titulo">Gente</div>
          {!miembros && !error && <Cargando filas={3} />}
          {miembros && miembros.length === 0 && (
            <Vacio titulo="No hay nadie dado de alta">
              <p>
                Quien administre el servidor en Discord entra igual. A las jefaturas se les da
                acceso aquí, pegando su ID de Discord: no hace falta que hayan entrado antes.
              </p>
            </Vacio>
          )}
          {miembros && miembros.length > 0 && (
            <ul className="lista">
              {miembros.map((m) => (
                <li className="fila" key={m.id}>
                  <Avatar nombre={m.persona.nombre} grande />
                  <div className="fila__principal">
                    <div className="fila__titulo">{m.persona.nombre}</div>
                    <div className="fila__meta">
                      {m.departamentos.length === 0 && <span>Sin departamento</span>}
                      {m.departamentos.map((d) => (
                        <span key={d.departamento_id}>
                          {d.nombre}{d.dirige ? ' (dirige)' : ''}
                        </span>
                      ))}
                      {!m.ha_entrado && <span className="etiqueta">Aún no ha entrado</span>}
                    </div>
                  </div>

                  {gestiona ? (
                    <>
                      <select
                        aria-label={`Papel de ${m.persona.nombre}`}
                        value={m.papel}
                        style={{ width: 'auto' }}
                        onChange={(e) => void cambiarPapel(m, e.target.value as Papel)}
                      >
                        {(Object.keys(PAPEL) as Papel[]).map((p) => (
                          <option key={p} value={p}>{PAPEL[p]}</option>
                        ))}
                      </select>
                      <button
                        className="boton boton--plano"
                        onClick={() => void darDeBaja(m)}
                        aria-label={`Dar de baja a ${m.persona.nombre}`}
                      >
                        Baja
                      </button>
                    </>
                  ) : (
                    <span className={`etiqueta papel--${m.papel}`}>{PAPEL[m.papel]}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {gestiona && miembros && departamentos.length > 0 && (
          <section className="tarjeta" style={{ marginTop: 16 }}>
            <div className="tarjeta__titulo">Quién está en cada departamento</div>
            <div className="tarjeta__cuerpo">
              {miembros.map((m) => (
                <div key={m.id} style={{ marginBottom: 12 }}>
                  <strong style={{ fontSize: '0.88rem' }}>{m.persona.nombre}</strong>
                  <div className="fila-botones" style={{ marginTop: 5 }}>
                    {departamentos.map((d) => {
                      const suyo = m.departamentos.find((p) => p.departamento_id === d.id)
                      return (
                        <button
                          key={d.id}
                          className={`boton${suyo ? ' boton--principal' : ''}`}
                          style={{ fontSize: '0.8rem' }}
                          onClick={() => {
                            // Tres pasos en un botón: fuera → dentro → dirige → fuera.
                            if (!suyo) void cambiarPertenencia(m, d.id, true, false)
                            else if (!suyo.dirige) void cambiarPertenencia(m, d.id, true, true)
                            else void cambiarPertenencia(m, d.id, false, false)
                          }}
                        >
                          {d.nombre}{suyo?.dirige ? ' ★' : ''}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
              <div className="campo__ayuda">
                Pulsa una vez para meter a alguien, otra para ponerle como jefatura (★), y otra
                para sacarle. <strong>Sólo las jefaturas ven todas las solicitudes de su
                departamento.</strong>
              </div>
            </div>
          </section>
        )}
      </div>

      {dialogo === 'alta' && (
        <DarDeAlta
          alCerrar={() => setDialogo(null)}
          alHecho={() => {
            setDialogo(null)
            void cargar()
          }}
        />
      )}

      {dialogo === 'departamento' && (
        <NuevoDepartamento
          alCerrar={() => setDialogo(null)}
          alHecho={() => {
            setDialogo(null)
            void recargarDepartamentos()
          }}
        />
      )}
    </>
  )
}

function DarDeAlta({ alCerrar, alHecho }: { alCerrar: () => void; alHecho: () => void }) {
  const { ruta, avisar, departamentos } = usePortal()
  // Lo normal es dar de alta a una jefatura que aún no ha entrado nunca: se pega su ID de Discord.
  const [modo, setModo] = useState<'id' | 'entrado'>('id')
  const [candidatos, setCandidatos] = useState<Persona[] | null>(null)
  const [quien, setQuien] = useState('')
  const [idSuelto, setIdSuelto] = useState('')
  const [papel, setPapel] = useState<Papel>('requester')
  const [departamento, setDepartamento] = useState('')
  const [dirige, setDirige] = useState(false)
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [hecho, setHecho] = useState<Miembro | null>(null)
  const [copiado, setCopiado] = useState(false)

  useEffect(() => {
    if (modo !== 'entrado' || candidatos !== null) return
    api
      .get<Persona[]>(`${ruta}/candidatos`)
      .then(setCandidatos)
      .catch((e: ErrorDeApi) => setError(e.message))
  }, [modo, candidatos, ruta])

  const id = modo === 'id' ? idSuelto.trim() : quien
  const idValido = modo === 'entrado' ? !!quien : /^\d{17,20}$/.test(id)

  async function enviar() {
    if (!idValido) {
      setError(modo === 'id'
        ? 'Ese no parece un ID de Discord: son entre 17 y 20 cifras, sin letras ni espacios.'
        : 'Elige a quién.')
      return
    }
    setEnviando(true)
    setError('')
    try {
      const creado = await api.post<Miembro>(`${ruta}/equipo`, {
        user_id: id,
        papel,
        departamento_id: departamento ? Number(departamento) : null,
        dirige: departamento ? dirige : false,
      })
      setHecho(creado)
    } catch (e) {
      setError((e as ErrorDeApi).message)
    } finally {
      setEnviando(false)
    }
  }

  async function copiar() {
    try {
      await navigator.clipboard.writeText(window.location.origin)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2000)
    } catch {
      avisar('No se ha podido copiar. Selecciónalo y cópialo a mano.', 'malo')
    }
  }

  if (hecho) {
    return (
      <Dialogo
        titulo="Dado de alta"
        alCerrar={alHecho}
        pie={<button className="boton boton--principal" onClick={alHecho}>Hecho</button>}
      >
        <p>
          <strong>{hecho.persona.nombre}</strong> ya tiene acceso como{' '}
          <strong>{PAPEL[hecho.papel].toLowerCase()}</strong>
          {hecho.departamentos[0] &&
            ` en ${hecho.departamentos[0].nombre}${hecho.departamentos[0].dirige ? ' (dirige)' : ''}`}
          .
        </p>
        {hecho.ha_entrado ? (
          <p className="campo__ayuda">Ya ha entrado antes, así que puede usar el portal ahora mismo.</p>
        ) : (
          <>
            <p>
              Todavía no ha entrado nunca. Pásale este enlace y dile que inicie sesión con su
              cuenta de Discord: al entrar verá directamente las solicitudes.
            </p>
            <div className="acceso__enlace">
              <input readOnly value={window.location.origin} onFocus={(e) => e.target.select()}
                     aria-label="Enlace del portal" />
              <button className="boton" onClick={() => void copiar()}>
                {copiado ? 'Copiado' : 'Copiar'}
              </button>
            </div>
          </>
        )}
      </Dialogo>
    )
  }

  return (
    <Dialogo
      titulo="Dar acceso al portal"
      alCerrar={alCerrar}
      pie={
        <>
          <button className="boton" onClick={alCerrar} disabled={enviando}>Cancelar</button>
          <button className="boton boton--principal" onClick={enviar} disabled={enviando || !idValido}>
            {enviando ? 'Guardando…' : 'Dar acceso'}
          </button>
        </>
      }
    >
      {error && <Error>{error}</Error>}

      <div className="fila-botones" role="tablist" style={{ marginBottom: 14 }}>
        <button role="tab" aria-selected={modo === 'id'}
                className={`boton${modo === 'id' ? ' boton--principal' : ''}`}
                onClick={() => { setModo('id'); setError('') }}>
          Por su ID de Discord
        </button>
        <button role="tab" aria-selected={modo === 'entrado'}
                className={`boton${modo === 'entrado' ? ' boton--principal' : ''}`}
                onClick={() => { setModo('entrado'); setError('') }}>
          Alguien que ya ha entrado
        </button>
      </div>

      {modo === 'id' && (
        <div className="campo">
          <label htmlFor="idsuelto">ID de Discord</label>
          <input id="idsuelto" inputMode="numeric" autoFocus placeholder="1405125091704180848"
                 value={idSuelto} onChange={(e) => setIdSuelto(e.target.value.replace(/\s/g, ''))} />
          <div className="campo__ayuda">
            No hace falta que haya entrado antes. Para copiarlo: en Discord, Ajustes → Avanzado →
            activa el <em>Modo desarrollador</em>; después, clic derecho sobre la persona →{' '}
            <em>Copiar ID de usuario</em>.
          </div>
        </div>
      )}

      {modo === 'entrado' && (
        <div className="campo">
          <label htmlFor="quien">Quién</label>
          {candidatos === null && !error && <Cargando filas={1} />}
          {candidatos !== null && candidatos.length === 0 && (
            <Vacio titulo="No hay nadie más que haya entrado">
              <p>Usa «Por su ID de Discord» para darle acceso antes de que entre.</p>
            </Vacio>
          )}
          {candidatos !== null && candidatos.length > 0 && (
            <select id="quien" value={quien} onChange={(e) => setQuien(e.target.value)}>
              <option value="">Elige a alguien…</option>
              {candidatos.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          )}
        </div>
      )}

      <div className="campo">
        <label htmlFor="papel">Qué puede hacer</label>
        <select id="papel" value={papel} onChange={(e) => setPapel(e.target.value as Papel)}>
          {(['requester', 'member', 'staff', 'developer', 'admin'] as Papel[]).map((p) => (
            <option key={p} value={p}>{PAPEL[p]}</option>
          ))}
        </select>
        <div className="campo__ayuda">{PAPEL_EXPLICADO[papel]}</div>
      </div>

      {departamentos.length > 0 && (
        <div className="campo">
          <label htmlFor="cuerpo">Cuerpo o departamento</label>
          <select id="cuerpo" value={departamento}
                  onChange={(e) => { setDepartamento(e.target.value); if (!e.target.value) setDirige(false) }}>
            <option value="">Ninguno</option>
            {departamentos.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </select>
          {departamento && (
            <label className="campo__casilla">
              <input type="checkbox" checked={dirige} onChange={(e) => setDirige(e.target.checked)} />
              Es jefe o jefa de este cuerpo: verá <strong>todas</strong> las solicitudes de su cuerpo
            </label>
          )}
        </div>
      )}
    </Dialogo>
  )
}

function NuevoDepartamento({
  alCerrar,
  alHecho,
}: {
  alCerrar: () => void
  alHecho: () => void
}) {
  const { ruta, avisar } = usePortal()
  const [nombre, setNombre] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [color, setColor] = useState('#5865F2')
  const [rol, setRol] = useState('')
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  async function enviar() {
    if (!nombre.trim()) {
      setError('Ponle un nombre.')
      return
    }
    setEnviando(true)
    setError('')
    try {
      await api.post(`${ruta}/departamentos`, {
        nombre, descripcion, color, discord_role_id: rol.trim() || null,
      })
      avisar('Departamento creado.', 'bien')
      alHecho()
    } catch (e) {
      setError((e as ErrorDeApi).message)
      setEnviando(false)
    }
  }

  return (
    <Dialogo
      titulo="Nuevo departamento"
      alCerrar={alCerrar}
      pie={
        <>
          <button className="boton" onClick={alCerrar} disabled={enviando}>Cancelar</button>
          <button className="boton boton--principal" onClick={enviar} disabled={enviando}>
            {enviando ? 'Creando…' : 'Crear'}
          </button>
        </>
      }
    >
      {error && <Error>{error}</Error>}
      <div className="campo">
        <label htmlFor="nd">Nombre</label>
        <input id="nd" value={nombre} autoFocus onChange={(e) => setNombre(e.target.value)}
               placeholder="Policía" />
      </div>
      <div className="campo">
        <label htmlFor="dd">Descripción</label>
        <input id="dd" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
      </div>
      <div className="campo">
        <label htmlFor="cd">Color</label>
        <input id="cd" type="color" value={color} style={{ width: 70, padding: 2, height: 36 }}
               onChange={(e) => setColor(e.target.value)} />
      </div>
      <div className="campo">
        <label htmlFor="rd">Rol de Discord (opcional)</label>
        <input id="rd" value={rol} inputMode="numeric"
               onChange={(e) => setRol(e.target.value.replace(/\D/g, ''))}
               placeholder="1510689288306688180" />
        <div className="campo__ayuda">
          El identificador del rol. Sirve para atar el departamento a Discord más adelante.
        </div>
      </div>
    </Dialogo>
  )
}
